import React, { FormEvent, useContext } from 'react';
import cn from '@/utils/cn';
import I18n from '@/modules/i18n';
import { useModalManager } from '@/components/common/Modal';
import { TCITooltip } from '@/components/ui/Tooltip';
import SubmitModal from '@/components/ui/Modal/SubmitModal';
import DndAnswerContext from '@/components/common/Context';
import { Refresh } from '../../../../../../../../images/react';

interface ClearButtonProps {
  correctChoices: { [key: string]: string[] };
  currentChoices: { [key: string]: string[] };
  isComplete: boolean;
  savedChoices: { [key: string]: string[] };
}

const ClearButton = ({ correctChoices, currentChoices, isComplete, savedChoices }: ClearButtonProps) => {
  const { answerChoiceDispatch, hasReusableAnswerChoices } = useContext(DndAnswerContext);
  const { close, isOpen, open } = useModalManager();

  const incorrectlyPlacedChoices = Object.entries(currentChoices).flatMap(([key, values]) => {
    const correctValues = correctChoices[key];
    const savedValues = savedChoices[key] || [];

    return values.map((value) => {
      const isSavedToDbAndCorrect = savedValues.includes(value) && correctValues?.includes(value);

      if (isSavedToDbAndCorrect) return undefined;

      return {
        draggableChoiceId: value,
        sourceDropZoneId: key,
      };
    }).filter(choice => !!choice);
  });

  const isDisabled = incorrectlyPlacedChoices.length === 0 || isComplete;

  const handleClick = () => {
    if (isDisabled) return;
    open();
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    incorrectlyPlacedChoices.forEach(({ draggableChoiceId, sourceDropZoneId }) => {
      answerChoiceDispatch({
        payload: {
          draggableChoiceId,
          hasReusableAnswerChoices,
          sourceDropZoneId,
        },
        type: 'REMOVE',
      });
    });

    close();
  };

  return (
    <>
      {/* disablePortal: this button is inside a CSS-transformed (scaled) D&D container */}
      <TCITooltip disablePortal>
        <TCITooltip.Trigger asChild>
          <button
            aria-disabled={isDisabled}
            aria-label={I18n.t('clear_dropzones')}
            className={cn(
              'tw-box-border tw-inline-flex tw-items-center tw-justify-center',
              'tw-border tw-border-solid tw-rounded-md tw-border-tci-purple',
              'tw-p-1 clear-button',
              isDisabled
                ? 'tw-text-very-dark-grey tw-bg-light-grey tw-cursor-not-allowed'
                : 'tw-cursor-pointer tw-text-tci-purple tw-bg-white hover:tw-text-white hover:tw-bg-tci-purple',
            )}
            tabIndex={0}
            type="button"
            onClick={handleClick}
          >
            <Refresh aria-hidden className="tw-box-border tw-p-0.5 tw-text-inherit" fill="currentColor" />
          </button>
        </TCITooltip.Trigger>
        <TCITooltip.Content>
          {isComplete ? I18n.t('clear_dropzones_disabled') : I18n.t('clear_dropzones')}
        </TCITooltip.Content>
      </TCITooltip>
      <SubmitModal
        close={close}
        headerText={I18n.t('clear_dropzones')}
        isOpen={isOpen}
        onSubmit={handleSubmit}
      >
        <p className="tw-text-base">{I18n.t('clear_dropzones_message.line_1')}</p>
        <p className="tw-text-base">{I18n.t('clear_dropzones_message.line_2')}</p>
      </SubmitModal>
    </>
  );
};

export default ClearButton;
